/**
 * The name a downloaded PDF is saved under.
 *
 * A `blob:` URL carries no name of its own, so a download from one lands in the
 * reader's Files as a bare UUID unless the anchor says otherwise — and a
 * document saved off a portal is worth nothing if its name does not say what it
 * is months later.
 *
 * The file's own name is preferred where the URL has one, because that is what
 * the publisher called it; the document title is the fallback, punctuation
 * flattened so the result is a name every filesystem accepts.
 */
export const pdfFileName = (src: string, title: string): string => {
  const fromUrl = fileNameFromUrl(src);
  if (fromUrl !== '') return fromUrl;

  const slug = title
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return `${slug === '' ? 'document' : slug}.pdf`;
};

/** The last path segment, when it looks like a PDF. Query strings and the
 *  parameters a signed URL carries are stripped; a segment without a `.pdf` is
 *  not a name worth preferring over the document's own title. */
const fileNameFromUrl = (src: string): string => {
  const path = src.split(/[?#]/)[0] ?? '';
  const segment = path.split('/').pop() ?? '';

  if (!/\.pdf$/i.test(segment)) return '';

  // An encoded segment is what an upload with a space in its name becomes; a
  // malformed one is not worth throwing over, so the raw segment stands.
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};
